import type { Collection } from '@/app/actions/collection';
import type { ApiError } from '@/app/hooks/useError';
import type { UriTemplate } from '@/app/utils/request/api-routes';
import type { InteractionType } from '~/model/InteractionType';
import type { ProjectType } from '~/model/ProjectType';
import { useLocale } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { fetchCollection, fetchItem } from '@/app/actions/server';
import { useApiErrorContext } from '@/app/components/Provider';
import { checkIsAnnouncement } from '~/components/Interaction/utils';
import { AcceptLanguage } from '~/const/locale';
import { RequestStatus, useRequestState } from '~/hooks/request/useRequestState';
import { useProjectTabsContext } from '../components/provider/ProjectTabsProvider';
import { TabsFilterValue } from '../types/project/tabs';

export function useProjectInteractions({
  project,
  collections: baseCollections,
  interaction: baseInteraction,
}: {
  project: ProjectType;
  collections: Record<TabsFilterValue, Collection<InteractionType>>;
  interaction: InteractionType & ApiError;
}) {
  const locale = useLocale();

  const { setError } = useApiErrorContext();

  const {
    handleSuccess: handleItemSuccess,
    handleError: handleItemError,
    startRequest: startItemRequest,
    status: itemStatus,
  } = useRequestState();

  const {
    handleSuccess: handleCollectionSuccess,
    handleError: handleCollectionError,
    startRequest: startCollectionRequest,
    status: collectionStatus,
  } = useRequestState();

  const {
    activeInteraction,
    activeTabFilter,
    setBadge,
    setTab,
  } = useProjectTabsContext();

  const [collections, setCollections] = useState(baseCollections);
  const [interaction, setInteraction] = useState(baseInteraction);

  const fetchInteraction = useCallback(async () => {
    if (
      !activeInteraction
      || interaction?.['hydra:description']
      || activeInteraction === interaction?.id
    ) {
      return;
    }

    startItemRequest();

    const response = await fetchItem<InteractionType>({
      headers: {
        acceptLanguage: AcceptLanguage[locale],
      },
      pathname: `interactions/${activeInteraction}`,
      revalidate: 0,
      searchParams: {
        'groups[]': [
          'ed_interaction_user_contributions',
          'interaction_translations',
          'project_media',
          'read_interaction_parent',
        ],
      },
    });

    if (response['hydra:description']) {
      handleItemError();
      return setError(response);
    }

    setInteraction(response);
    handleItemSuccess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeInteraction]);

  const fetchInteractions = async (
    filter: TabsFilterValue,
    nextPage: UriTemplate,
  ) => {
    startCollectionRequest();

    const response = await fetchCollection<InteractionType>({
      headers: {
        acceptLanguage: AcceptLanguage[locale],
      },
      pathname: nextPage,
      revalidate: 0,
    });

    if (response['hydra:description']) {
      handleCollectionError();
      return setError(response);
    }

    setCollections((current) => {
      return {
        ...current,
        [filter]: {
          ...response,
          'hydra:member': [
            ...current[filter]['hydra:member'],
            ...response['hydra:member'],
          ],
        },
      };
    });

    handleCollectionSuccess();
  };

  useEffect(() => {
    fetchInteraction();
  }, [fetchInteraction]);

  useEffect(() => {
    if (activeInteraction && activeInteraction !== interaction?.id) {
      setInteraction(null);
    }
  }, [activeInteraction, interaction]);

  useEffect(() => {
    const items = collections[TabsFilterValue.NEWS];

    setBadge(current => ({
      ...current,
      [TabsFilterValue.NEWS]: {
        count: items?.['hydra:member']?.length || 0,
        hasMore: !!items?.['hydra:view']?.['hydra:next'],
      },
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collections[TabsFilterValue.NEWS]]);

  useEffect(() => {
    if (baseInteraction?.['hydra:description']) {
      return setError(baseInteraction);
    }

    if (activeTabFilter !== TabsFilterValue.ONGOING) {
      return;
    }

    const ongoing = baseCollections[TabsFilterValue.ONGOING]['hydra:member'];
    const hasInteractionInOngoing = !activeInteraction && ongoing?.some(
      item => item.id === baseInteraction?.id,
    );

    if ((!hasInteractionInOngoing && !!baseInteraction?.id) || !ongoing.length) {
      setTab(
        TabsFilterValue[checkIsAnnouncement(baseInteraction) ? 'NEWS' : 'STEPS'],
        baseInteraction?.id,
        false,
        !baseInteraction?.id,
      );
    } else if (baseInteraction) {
      setTab(TabsFilterValue.ONGOING, null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    collections,
    interaction,
    isFetchingItem: itemStatus === RequestStatus.PENDING,
    isFetchingCollection: collectionStatus === RequestStatus.PENDING,
    project,
    fetchInteractions,
  };
}
